/**
 * Ahrefs Web Analytics — privacy-friendly pageviews (no cookies).
 * Script: https://analytics.ahrefs.com/analytics.js
 * Docs: https://help.ahrefs.com/en/articles/10247870-about-ahrefs-web-analytics
 *
 * Official snippet uses data-key on a static <script> in <head>.
 * This module: ensures the tag exists + fires SPA pageviews via
 * window.AhrefsAnalytics.sendEvent (patched history already, but RR
 * edge cases / late init need an explicit call).
 */

const AHREFS_SCRIPT_SRC = "https://analytics.ahrefs.com/analytics.js";

/** Build-time key; falls back to production project key for delfuocampo. */
export const AHREFS_ANALYTICS_KEY = (
  (import.meta.env.VITE_AHREFS_ANALYTICS_KEY as string | undefined)?.trim() ||
  "c8zVBQNULX2vKEKSfHWRAQ"
).trim();

type AhrefsApi = {
  sendEvent: (
    name: string,
    opts?: { meta?: Record<string, unknown>; props?: Record<string, unknown>; callback?: () => void },
  ) => void;
};

function getAhrefsApi(): AhrefsApi | null {
  if (typeof window === "undefined") return null;
  const api = (window as Window & { AhrefsAnalytics?: AhrefsApi }).AhrefsAnalytics;
  return api && typeof api.sendEvent === "function" ? api : null;
}

/**
 * Ensure the official script tag is in <head> with data-key.
 * Prefer the static tag from index.html; only inject if missing.
 *
 * Note: dynamically injected scripts may not set document.currentScript
 * reliably — static head tag is the primary install path.
 */
export function ensureAhrefsScript(): void {
  if (typeof document === "undefined" || !AHREFS_ANALYTICS_KEY) return;
  if (document.querySelector(`script[src*="analytics.ahrefs.com"]`)) return;

  const s = document.createElement("script");
  s.src = AHREFS_SCRIPT_SRC;
  s.async = true;
  s.setAttribute("data-key", AHREFS_ANALYTICS_KEY);
  // Helps Ahrefs attribute the property if multi-site account
  s.setAttribute("data-instance", "AhrefsAnalytics");
  document.head.appendChild(s);
}

/** Fire a pageview (SPA route change or recovery after late script load). */
export function trackAhrefsPageview(): void {
  if (!AHREFS_ANALYTICS_KEY) return;
  ensureAhrefsScript();

  const api = getAhrefsApi();
  if (api) {
    try {
      api.sendEvent("pageview");
    } catch {
      /* ignore */
    }
    return;
  }

  // Script still loading — retry once shortly after
  window.setTimeout(() => {
    try {
      getAhrefsApi()?.sendEvent("pageview");
    } catch {
      /* ignore */
    }
  }, 1500);
}

/** Call once on app boot (with GA/Metricool idle path). */
export function initAhrefsAnalytics(): void {
  if (typeof window === "undefined" || !AHREFS_ANALYTICS_KEY) return;
  ensureAhrefsScript();
  // Initial pageview is auto-fired by analytics.js; only ensure script is present.
}
